import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  PieChart, 
  BarChart3,
  AlertTriangle,
  CheckCircle,
  Info,
  Plus,
  Minus
} from 'lucide-react';

interface Investment {
  id: string;
  name: string;
  type: 'cash' | 'deposits' | 'bonds' | 'stocks' | 'funds' | 'real_estate';
  amount: number;
  returnRate: number;
  riskLevel: 'low' | 'medium' | 'high';
  description: string;
  icon: string;
  color: string;
}

interface InvestmentPortfolioProps {
  totalSavings: number;
  currentInvestments: Investment[];
  onInvest: (investmentId: string, amount: number) => void;
  onWithdraw: (investmentId: string, amount: number) => void;
  className?: string;
}

const InvestmentPortfolio: React.FC<InvestmentPortfolioProps> = ({
  totalSavings,
  currentInvestments,
  onInvest,
  onWithdraw,
  className = ""
}) => {
  const [selectedInvestment, setSelectedInvestment] = useState<string | null>(null);
  const [investmentAmount, setInvestmentAmount] = useState<number>(0);

  const availableInvestments: Investment[] = [
    {
      id: 'cash',
      name: 'Cash Savings',
      type: 'cash',
      amount: 0,
      returnRate: 0.02,
      riskLevel: 'low',
      description: 'Safe but low returns. Good for emergency funds.',
      icon: '💵',
      color: 'from-green-500 to-green-600'
    },
    {
      id: 'deposits',
      name: 'Bank Deposits',
      type: 'deposits',
      amount: 0,
      returnRate: 0.04,
      riskLevel: 'low',
      description: 'Low risk with guaranteed returns. FDIC insured.',
      icon: '🏦',
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'bonds',
      name: 'Government Bonds',
      type: 'bonds',
      amount: 0,
      returnRate: 0.06,
      riskLevel: 'low',
      description: 'Very safe government-backed securities.',
      icon: '📜',
      color: 'from-purple-500 to-purple-600'
    },
    {
      id: 'funds',
      name: 'Index Funds',
      type: 'funds',
      amount: 0,
      returnRate: 0.08,
      riskLevel: 'medium',
      description: 'Diversified portfolio with moderate risk.',
      icon: '📊',
      color: 'from-orange-500 to-orange-600'
    },
    {
      id: 'stocks',
      name: 'Stock Market',
      type: 'stocks',
      amount: 0,
      returnRate: 0.12,
      riskLevel: 'high',
      description: 'High potential returns with significant risk.',
      icon: '📈',
      color: 'from-red-500 to-red-600'
    },
    {
      id: 'real_estate',
      name: 'Real Estate',
      type: 'real_estate',
      amount: 0,
      returnRate: 0.10,
      riskLevel: 'medium',
      description: 'Property investment with rental income potential.',
      icon: '🏠',
      color: 'from-yellow-500 to-yellow-600'
    }
  ];

  // Calculate portfolio statistics
  const totalInvested = currentInvestments.reduce((sum, inv) => sum + inv.amount, 0);
  const availableCash = totalSavings - totalInvested;
  const totalValue = currentInvestments.reduce((sum, inv) => {
    const currentValue = inv.amount * (1 + inv.returnRate);
    return sum + currentValue;
  }, 0);
  const totalGains = totalValue - totalInvested;

  // Calculate portfolio allocation
  const getPortfolioAllocation = () => {
    const allocation = currentInvestments.map(inv => ({
      ...inv,
      percentage: totalInvested > 0 ? (inv.amount / totalInvested) * 100 : 0
    }));
    return allocation;
  };

  const portfolioAllocation = getPortfolioAllocation();

  // Get risk level color
  const getRiskColor = (risk: string) => {
    switch (risk) {
      case 'low': return 'text-green-400 bg-green-500/20';
      case 'medium': return 'text-yellow-400 bg-yellow-500/20';
      case 'high': return 'text-red-400 bg-red-500/20';
      default: return 'text-slate-400 bg-slate-500/20';
    }
  };

  const handleInvest = () => {
    if (selectedInvestment && investmentAmount > 0 && investmentAmount <= availableCash) {
      onInvest(selectedInvestment, investmentAmount);
      setInvestmentAmount(0);
      setSelectedInvestment(null);
    }
  };

  const handleWithdraw = (investmentId: string) => {
    const investment = currentInvestments.find(inv => inv.id === investmentId);
    if (investment && investment.amount > 0) {
      onWithdraw(investmentId, investment.amount);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-slate-800/50 rounded-2xl border border-slate-700 p-6 ${className}`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-xl">
            <PieChart size={24} className="text-white" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-white">Investment Portfolio</h3>
            <p className="text-sm text-slate-400">Manage your investments and savings</p>
          </div>
        </div>
      </div>

      {/* Portfolio Summary */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="p-3 bg-slate-700/50 rounded-xl border border-slate-600">
          <div className="flex items-center space-x-1 mb-1">
            <DollarSign size={14} className="text-green-400" />
            <span className="text-xs font-medium text-white">Total Savings</span>
          </div>
          <div className="text-lg font-bold text-green-400">
            ${totalSavings.toLocaleString()}
          </div>
        </div>

        <div className="p-3 bg-slate-700/50 rounded-xl border border-slate-600">
          <div className="flex items-center space-x-1 mb-1">
            {totalGains >= 0 ? (
              <TrendingUp size={14} className="text-green-400" />
            ) : (
              <TrendingDown size={14} className="text-red-400" />
            )}
            <span className="text-xs font-medium text-white">Gains/Losses</span>
          </div>
          <div className={`text-lg font-bold ${totalGains >= 0 ? 'text-green-400' : 'text-red-400'}`}>
            ${totalGains.toLocaleString()}
          </div>
        </div>
      </div>

      {/* Portfolio Allocation */}
      {portfolioAllocation.length > 0 && (
        <div className="mb-4">
          <h4 className="text-sm font-semibold text-white mb-2">Portfolio Allocation</h4>
          <div className="space-y-2">
            {portfolioAllocation.map((investment) => (
              <motion.div
                key={investment.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-center justify-between p-2 bg-slate-700/50 rounded-lg"
              >
                <div className="flex items-center space-x-2">
                  <span className="text-lg">{investment.icon}</span>
                  <div>
                    <div className="text-sm font-medium text-white">{investment.name}</div>
                    <div className="text-xs text-slate-400">
                      ${investment.amount.toLocaleString()} • {investment.percentage.toFixed(1)}%
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-1">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(investment.riskLevel)}`}>
                    {investment.riskLevel}
                  </span>
                  <button
                    onClick={() => handleWithdraw(investment.id)}
                    className="p-1 text-red-400 hover:text-red-300 transition-colors"
                  >
                    <Minus size={14} />
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      )}

      {/* Investment Options */}
      <div className="mb-4">
        <h4 className="text-sm font-semibold text-white mb-2">Available Investments</h4>
        <div className="grid grid-cols-1 gap-2">
          {availableInvestments.map((investment) => (
            <motion.div
              key={investment.id}
              whileHover={{ scale: 1.02, y: -2 }}
              className={`p-3 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
                selectedInvestment === investment.id
                  ? 'border-blue-500 bg-blue-500/10'
                  : 'border-slate-600 bg-slate-700/50 hover:border-slate-500'
              }`}
              onClick={() => setSelectedInvestment(investment.id)}
            >
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-lg">{investment.icon}</span>
                <div className="flex-1">
                  <h5 className="text-sm font-semibold text-white">{investment.name}</h5>
                  <div className="flex items-center space-x-2">
                    <span className="text-xs text-green-400">
                      {Math.round(investment.returnRate * 100)}% return
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRiskColor(investment.riskLevel)}`}>
                      {investment.riskLevel}
                    </span>
                  </div>
                </div>
              </div>
              <p className="text-xs text-slate-400">{investment.description}</p>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Investment Controls */}
      <AnimatePresence>
        {selectedInvestment && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="p-3 bg-slate-700/50 rounded-xl border border-slate-600"
          >
            <h5 className="text-sm font-semibold text-white mb-2">Invest in {availableInvestments.find(inv => inv.id === selectedInvestment)?.name}</h5>
            <div className="flex items-center space-x-2">
              <div className="flex-1">
                <input
                  type="number"
                  value={investmentAmount}
                  onChange={(e) => setInvestmentAmount(Number(e.target.value))}
                  max={availableCash}
                  min={0}
                  className="w-full px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-sm focus:outline-none focus:border-blue-500"
                  placeholder="Amount"
                />
                <div className="text-xs text-slate-400 mt-1">
                  Available: ${availableCash.toLocaleString()}
                </div>
              </div>
              <button
                onClick={handleInvest}
                disabled={investmentAmount <= 0 || investmentAmount > availableCash}
                className={`px-3 py-1 rounded text-sm font-semibold transition-all duration-300 ${
                  investmentAmount > 0 && investmentAmount <= availableCash
                    ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white'
                    : 'bg-slate-600 text-slate-400 cursor-not-allowed'
                }`}
              >
                <Plus size={14} className="inline mr-1" />
                Invest
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Investment Tips */}
      <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
        <div className="flex items-start space-x-3">
          <Info size={20} className="text-blue-400 mt-0.5" />
          <div>
            <h5 className="text-sm font-medium text-blue-400 mb-1">Investment Tips</h5>
            <ul className="text-xs text-slate-300 space-y-1">
              <li>• Diversify your portfolio across different asset classes</li>
              <li>• Start with low-risk investments and gradually increase risk</li>
              <li>• Keep an emergency fund in cash or deposits</li>
              <li>• Consider your time horizon when choosing investments</li>
              <li>• Higher returns usually come with higher risks</li>
            </ul>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default InvestmentPortfolio;
